import * as yup from 'yup';
// Types
import {
  CampaignTaskType,
  CampaignUsageRightsScope,
  CbApplicationQuestionType,
  CbDueDateType,
} from 'api/graphql-global-types';
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity,
} from 'api/campaignV2/types/GetCampaignsV2';
import { TimeLineDeliverable } from 'helpers/deliverables';
// Helpers
import {
  mapEditingItemToFormData,
  TimelineDeliverableFormData,
  validateStep1,
  validateStep2,
} from 'helpers/timelineDeliverables';
import { mapUgcEditingDeliverableToFormData } from 'helpers/campaignUgc';
import {
  CustomCampaignFormValues,
  CustomCampaignValidationValues,
  getCustomSearch,
  toNullableNumber,
} from 'helpers/customCampaign';
// Utils
import {
  PlatformCriteriaItem,
  PromoType,
} from 'helpers/customCampaignConstants';

export type CustomCampaignRawFormValues = Partial<
  Record<keyof CustomCampaignFormValues, any>
>;

type BuildSubmitValuesArgs = {
  values: CustomCampaignRawFormValues;
  isPrivate: boolean;
  noUsageRights: boolean;
};

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

const nullableNumberSchema = () =>
  yup
    .number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || Number.isNaN(value) ? null : value
    );

const nullableStringSchema = () =>
  yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value));

const getInvalidDeliverableIndex = (
  deliverables?: Array<TimeLineDeliverable | undefined>
): number => {
  if (!deliverables?.length) return -1;

  return deliverables.findIndex((deliverable: any) => {
    if (deliverable.type === CampaignTaskType.APPEARANCE) {
      const appearance = deliverable.appearance ?? deliverable;
      const appearanceDate =
        appearance.appearanceDate ?? deliverable.dueDate?.fixedDate;

      return isBlank(deliverable.title) || isBlank(appearanceDate);
    }

    if (
      deliverable.type === CampaignTaskType.UGC ||
      deliverable.type === CampaignTaskType.CONTENT
    ) {
      const formData = mapUgcEditingDeliverableToFormData(deliverable as any);
      const hasInvalidContentPiece = formData.contentPieces.some(
        (piece) =>
          isBlank(piece.mediaType) ||
          isBlank(piece.title) ||
          isBlank(piece.description)
      );

      if (
        isBlank(formData.name) ||
        isBlank(formData.description) ||
        isBlank(formData.dueDateType) ||
        hasInvalidContentPiece
      ) {
        return true;
      }

      if (formData.dueDateType === CbDueDateType.APPLICATION_BASED) {
        return (
          isBlank(formData.approvalNumber) || isBlank(formData.approvalUnit)
        );
      }

      if (formData.dueDateType === 'specific') {
        if (isBlank(formData.dateRangeType)) return true;

        if (formData.dateRangeType === CbDueDateType.FIXED) {
          return isBlank(formData.singleDateType) || isBlank(formData.dueDate);
        }

        if (formData.dateRangeType === CbDueDateType.RANGE) {
          return isBlank(formData.startDate) || isBlank(formData.endDate);
        }
      }

      return false;
    }

    const formData = mapEditingItemToFormData(deliverable as any);
    return (
      !validateStep1(formData as TimelineDeliverableFormData) ||
      !validateStep2(formData as TimelineDeliverableFormData)
    );
  });
};

export const customCampaignValidationSchema = yup
  .object()
  .shape({
    name: nullableStringSchema()
      .nullable()
      .max(100, 'Campaign name must be at most 100 characters'),
    internalName: nullableStringSchema()
      .nullable()
      .max(100, 'Internal campaign name must be at most 100 characters'),
    brandName: nullableStringSchema().nullable(),
    website: nullableStringSchema()
      .nullable()
      .url('Please enter a valid company website URL'),
    instructions: nullableStringSchema()
      .nullable()
      .max(5000, 'Campaign brief must be at most 5000 characters'),
    encouraged: nullableStringSchema()
      .nullable()
      .max(5000, "DO's must be at most 5000 characters"),
    avoid: nullableStringSchema()
      .nullable()
      .max(5000, "DON'Ts must be at most 5000 characters"),
    flatFeePerStore: nullableNumberSchema()
      .min(0, 'Price per athlete must be at least $0')
      .max(999999, 'Price per athlete must be at most $999,999'),
    applicationDeadlineDays: nullableNumberSchema()
      .integer('Application window must be a whole number')
      .min(1, 'Application window must be at least 1 day')
      .max(999, 'Application window must be at most 999 days'),
    usageRightsText: nullableStringSchema().nullable(),
    usageRightsDurationMonths: nullableNumberSchema()
      .integer('Usage rights duration must be a whole number')
      .min(1, 'Usage rights duration must be at least 1 month')
      .max(120, 'Usage rights duration must be at most 120 months'),
    usageRightsScopes: yup.array().of(yup.mixed<CampaignUsageRightsScope>()),
    questions: yup
      .array()
      .of(yup.string().trim().min(1, 'Please enter a question.').max(5000))
      .max(5, 'You can add at most 5 questions')
      .nullable(),
    ageRanges: yup
      .array()
      .of(
        yup.object().shape({
          minAge: nullableNumberSchema()
            .required('Min age is required')
            .min(0, 'Min age must be 0 or greater'),
          maxAge: nullableNumberSchema()
            .min(0, 'Max age must be 0 or greater')
            .test(
              'min-lte-max',
              'Max age must be greater than or equal to min age',
              function (maxAge) {
                const minAge = toNullableNumber(
                  (this.parent as { minAge?: unknown }).minAge
                );
                if (minAge == null || maxAge == null) return true;
                return minAge <= maxAge;
              }
            ),
        })
      )
      .nullable(),
    platformCriteria: yup
      .array()
      .of(
        yup.object().shape({
          minFollowers: nullableNumberSchema().min(
            0,
            'Min followers must be 0 or greater'
          ),
          maxFollowers: nullableNumberSchema()
            .min(0, 'Max followers must be 0 or greater')
            .test(
              'min-followers-lte-max',
              'Max followers must be greater than or equal to min followers',
              function (maxFollowers) {
                const minFollowers = toNullableNumber(
                  (this.parent as PlatformCriteriaItem).minFollowers
                );
                if (minFollowers == null || maxFollowers == null) return true;
                return minFollowers <= maxFollowers;
              }
            ),
          minEngagementRate: nullableNumberSchema().min(
            0,
            'Min engagement rate must be 0 or greater'
          ),
        })
      )
      .nullable(),
    deliverables: yup
      .array()
      .of(yup.mixed<TimeLineDeliverable>())
      .min(1, 'You must add at least one deliverable.')
      .required('You must add at least one deliverable.')
      .test(
        'valid-custom-deliverables',
        'One or more deliverables are missing required fields.',
        function (deliverables) {
          if (!deliverables?.length) return true;

          const invalidIndex = getInvalidDeliverableIndex(deliverables);

          if (invalidIndex === -1) return true;

          return this.createError({
            message: `Deliverable ${
              invalidIndex + 1
            } is missing required fields. Please edit it before submitting.`,
          });
        }
      ),
  })
  .test(
    'required-custom-campaign-fields',
    'Please fill in all required fields',
    function (values) {
      const formValues = values as CustomCampaignValidationValues | undefined;
      if (!formValues) return true;

      const hasWholeFormFields =
        'name' in formValues ||
        'brandName' in formValues ||
        'flatFeePerStore' in formValues;
      if (!hasWholeFormFields) return true;

      const createRequiredError = (
        path: keyof CustomCampaignValidationValues,
        message: string
      ) => this.createError({ path: String(path), message });

      if (isBlank(formValues.name))
        return createRequiredError('name', 'Campaign name is required');
      if (isBlank(formValues.brandName))
        return createRequiredError('brandName', 'Brand name is required');
      if (isBlank(formValues.website))
        return createRequiredError('website', 'Website is required');
      if (isBlank(formValues.flatFeePerStore)) {
        return createRequiredError(
          'flatFeePerStore',
          'Price per athlete is required'
        );
      }

      if (formValues.usageRights === false) {
        if (isBlank(formValues.usageRightsDurationMonths)) {
          return createRequiredError(
            'usageRightsDurationMonths',
            'Usage rights duration is required'
          );
        }
        if (!formValues.usageRightsScopes?.length) {
          return createRequiredError(
            'usageRightsScopes',
            'Please select at least one usage rights scope'
          );
        }
      }

      if (!formValues.isPrivate) {
        if (!formValues.sports?.length)
          return createRequiredError(
            'sports',
            'Please select at least one sport'
          );
        if (!formValues.locations?.length) {
          return createRequiredError(
            'locations',
            'Please select at least one location'
          );
        }
        if (!formValues.platformCriteria?.length) {
          return createRequiredError(
            'platformCriteria',
            'Please select at least one social platform'
          );
        }
      }

      return true;
    }
  );

// Maps a campaign record into the antd form's initialValues. Centralizes the
// record → form-field mapping so the modal stays declarative.
export const buildCustomCampaignInitialValues = (
  record: GetCampaignsV2_getCampaigns_entities,
  activity: GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity | null,
  deliverablesChecklist: TimeLineDeliverable[]
): Record<string, unknown> => {
  const customSearch = getCustomSearch(record);

  const questions = record?.questions
    ?.filter((item) => item.type === CbApplicationQuestionType.Regular)
    ?.sort((a, b) => a.order - b.order);

  const contracts =
    activity?.contracts?.map((contract) => ({
      key: contract.key,
      url: contract.url,
      file: undefined,
    })) ?? [];

  return {
    isPrivate: record?.isPrivate ?? false,
    name: record?.name || '',
    internalName: record?.internalName || '',
    brandName: activity?.brandName || '',
    website: activity?.website || '',
    instructions: activity?.instructions || '',
    encouraged: activity?.encouraged || '',
    avoid: activity?.avoid || '',
    flatFeePerStore: record?.flatFeePerStore ?? null,
    applicationDeadlineDays: record?.applicationDeadlineDays || null,
    applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
    usageRights: !record?.usageRights,
    usageRightsText: record?.usageRightsText || null,
    usageRightsDurationMonths: record?.usageRightsDuration
      ? (record.usageRightsDuration.years ?? 0) * 12 +
          (record.usageRightsDuration.months ?? 0) || null
      : null,
    usageRightsScopes: record?.usageRightsScopes || [],
    promoType: activity?.promoUrls?.length ? PromoType.Link : PromoType.Code,
    promoValue: activity?.promoUrls?.[0] ?? activity?.promoCodes?.[0] ?? null,
    products:
      activity?.products?.map((product) => ({
        name: product.name,
        cost: product.cost,
        qty: product.qty,
        description: product.description,
        shipping: Boolean(product.shipping),
      })) ?? [],
    questions: questions?.map((item) => item.question) || [],
    contracts,
    // Talent / search filters
    userRole: customSearch?.roles || [],
    genders: customSearch?.genders || [],
    ageRanges:
      customSearch?.athleteAgeRanges?.map((range) => ({
        minAge: range.minAge,
        maxAge: range.maxAge,
      })) ??
      (customSearch?.athleteMinAge != null
        ? [
            {
              minAge: customSearch.athleteMinAge,
              maxAge: customSearch.athleteMaxAge ?? null,
            },
          ]
        : []),
    sports: customSearch?.sports || [],
    influencerCategoryIds: customSearch?.influencerCategoryIds || [],
    locations: customSearch?.locations || [],
    hometownLocations: customSearch?.hometownLocations || [],
    excludedLocations: customSearch?.excludedLocations || [],
    searchHashtags: customSearch?.hashtags
      ? customSearch.hashtags.map((tag) => ({
          label: tag,
          value: tag,
          storeCount: 0,
        }))
      : [],
    excludePreviouslyWorkedWith: Boolean(customSearch?.excludeWorkedWith),
    excludePreviouslyDeclined: Boolean(customSearch?.excludeDeclined),
    // Audience filters
    audienceGender: customSearch?.audienceGender || null,
    audienceAgeRanges: customSearch?.audienceAgeRanges || [],
    audienceEthnicities: customSearch?.audienceEthnicities || [],
    audienceInterests: customSearch?.audienceInterests || [],
    audienceLocations: customSearch?.audienceLocations || [],
    deliverables: deliverablesChecklist,
  };
};

// Maps the live antd form values into the typed CustomCampaignFormValues used
// to build the update mutation input. Normalizes numeric strings to numbers.
export const buildCustomCampaignSubmitValues = ({
  values,
  isPrivate,
  noUsageRights,
}: BuildSubmitValuesArgs): CustomCampaignFormValues => ({
  name: values.name,
  internalName: values.internalName,
  isPrivate,
  brandName: values.brandName,
  website: values.website,
  instructions: values.instructions,
  encouraged: values.encouraged,
  avoid: values.avoid,
  flatFeePerStore: isBlank(values.flatFeePerStore)
    ? null
    : Number(values.flatFeePerStore),
  applicationDeadlineDays: values.applicationDeadlineDays
    ? Number(values.applicationDeadlineDays)
    : null,
  usageRights: noUsageRights,
  usageRightsText: values.usageRightsText,
  usageRightsDurationMonths: values.usageRightsDurationMonths
    ? Number(values.usageRightsDurationMonths)
    : null,
  usageRightsScopes: values.usageRightsScopes,
  promoType: values.promoType,
  promoValue: values.promoValue,
  products: values.products || [],
  questions: values.questions || [],
  userRole: values.userRole,
  sports: values.sports,
  genders: values.genders,
  ageRanges: values.ageRanges || [],
  influencerCategoryIds: values.influencerCategoryIds,
  locations: values.locations,
  hometownLocations: values.hometownLocations,
  excludedLocations: values.excludedLocations,
  searchHashtags: values.searchHashtags,
  excludePreviouslyWorkedWith: values.excludePreviouslyWorkedWith,
  excludePreviouslyDeclined: values.excludePreviouslyDeclined,
  audienceGender: values.audienceGender,
  audienceAgeRanges: values.audienceAgeRanges,
  audienceEthnicities: values.audienceEthnicities,
  audienceInterests: values.audienceInterests,
  audienceLocations: values.audienceLocations,
});
